import * as _ from 'lodash';

import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeUiTypeDetails } from 'src/app/shared/models/attributes/attributeUiTypeDetails';
import { AttributeEntityType } from './attributeEntityType';
import { AttributeEntityTypeMetadata } from 'src/app/admin/admin-home/custom-fields/attributeEntityTypeMetadata';
import { AttributeDefinitionCondition } from './attributeDefinitionCondition';
import { AttributeDefinitionWorkspace } from '../workspace/attributeDefinitionWorkspace';
import { AttributeDefinitionSubFieldItem } from './attributeDefinitionSubFieldItem';
import { AttributeDefinitionWrite } from './attributeDefinitionWrite';
import { SearchValueType } from '../../components/odata-filter/models/search-value-type';

export class AttributeDefinition {
  public id: string;
  public entityType: string;
  public uiType: string;
  public name: string;
  public description: string;
  public adminDocumentation: string;
  public formPageId: string;
  public filterable: boolean;
  public featured: boolean;
  public required: boolean;
  public isReadOnly: boolean;
  public isHidden: boolean;
  public isArchived: boolean = false;
  public hasVisualization: boolean = false;
  public showOnCreate: boolean;
  public isSubField: boolean;
  public min: string;
  public max: string;
  public allowsUserAddedValues: boolean;
  public valueList: AttributeDefinitionValueListItem[];
  public subFieldList: AttributeDefinitionSubFieldItem[];
  public conditions: AttributeDefinitionCondition[];
  public workspaces: AttributeDefinitionWorkspace[];
  createdBy: string;
  createdOn: Date;
  updatedBy: string;
  updatedOn: Date;

  constructor(
    id: string,
    entityType: string,
    uiType: string,
    name: string,
    description: string,
    adminDocumentation: string,
    filterable: boolean,
    featured: boolean,
    required: boolean,
    isReadOnly: boolean,
    isHidden: boolean,
    hasVisualization: boolean,
    showOnCreate: boolean,
    isSubField: boolean,
    min: string,
    max: string,
    allowsUserAddedValues: boolean,
    valueList: AttributeDefinitionValueListItem[],
    subFieldList: AttributeDefinitionSubFieldItem[],
    workspaces: AttributeDefinitionWorkspace[],
    createdBy: string,
    createdOn: Date,
    updatedBy: string,
    updatedOn: Date,
    isArchived: boolean = false
  ) {
    this.id = id;
    this.entityType = entityType;
    this.uiType = uiType;
    this.name = name;
    this.description = description;
    this.adminDocumentation = adminDocumentation;
    this.filterable = this.canBeFiltered ? filterable : false;
    this.featured = featured;
    this.required = required;
    this.isReadOnly = isReadOnly;
    this.isHidden = isHidden;
    this.hasVisualization = hasVisualization;
    this.showOnCreate = showOnCreate;
    this.isSubField = isSubField;
    this.min = min;
    this.max = max;
    this.allowsUserAddedValues = allowsUserAddedValues;
    this.valueList = valueList ? _.orderBy(valueList, 'rank') : valueList;
    this.subFieldList = subFieldList ? _.orderBy(subFieldList, 'rank') : subFieldList;
    this.workspaces = workspaces;
    this.createdBy = createdBy;
    this.createdOn = createdOn;
    this.updatedBy = updatedBy;
    this.updatedOn = updatedOn;
    this.isArchived = isArchived;
  }

  get allowsMultipleValues(): boolean {
    return this.isOfUiType([AttributeUiType.Checkboxes, AttributeUiType.MultiSelect]);
  }

  get hasValueList(): boolean {
    return this.valueList && !_.isEmpty(this.valueList);
  }

  get isEnumerated(): boolean {
    return this.uiTypeDetails.isEnumerated;
  }

  get isSupportedForSubFieldItems(): boolean {
    return this.uiTypeDetails.isSupportedForSubFieldItems && this.isSubField;
  }

  get hasDescription(): boolean {
    return this.entityTypeMetadata.hasDescription;
  }

  public isOfUiType(uiTypes: Array<AttributeUiType>): boolean {
    return _.some(uiTypes, (uiType) => uiType == this.uiType);
  }

  public getValueListItem(value: string) {
    let item;
    item = _.find(this.valueList, { id: value });

    return item ? item : _.find(this.valueList, { itemValue: value });
  }

  public moveValueListItem(oldIndex: number, newIndex: number) {
    let movedValue = this.valueList.splice(oldIndex, 1);
    this.valueList.splice(newIndex, 0, ...movedValue);
    this.valueList.map((listItem, index) => {
      listItem.rank = index + 1;
    });
  }

  public getIcon(): string {
    return this.uiTypeDetails.icon;
  }

  public getUiName(): string {
    return this.uiTypeDetails.name;
  }

  private get uiTypeDetails(): AttributeUiTypeDetails {
    return new AttributeUiTypeDetails(this.uiType as AttributeUiType);
  }

  private get entityTypeMetadata(): AttributeEntityTypeMetadata {
    return AttributeEntityTypeMetadata.get(this.entityType as AttributeEntityType);
  }

  private get canBeFiltered(): boolean {
    return new AttributeUiTypeDetails(this.uiType as AttributeUiType).canBeFiltered();
  }

  public buildCloneModel(): AttributeDefinitionWrite {
    let writeModel = new AttributeDefinitionWrite();
    var entityType = AttributeEntityType[this.entityType];
    var uiType = AttributeUiType[this.uiType];
    writeModel.entityType = entityType;
    writeModel.uiType = uiType;
    writeModel.name = `${this.name} (copy)`;
    writeModel.description = this.description;
    writeModel.adminDocumentation = this.adminDocumentation;
    writeModel.isSubField = this.isSubField;
    if (this.isEnumerated && entityType === AttributeEntityType.Person) {
      writeModel.featured = this.featured;
      writeModel.hasVisualization = this.hasVisualization;
    }
    writeModel.filterable = this.filterable;
    if (entityType === AttributeEntityType.Team && uiType !== AttributeUiType.Document) {
      writeModel.showOnCreate = this.showOnCreate;
    }
    writeModel.required = this.required;
    writeModel.valueList = [];

    writeModel.valueList = this.valueList.map((x) => {
      return {
        itemValue: x.itemValue,
        itemDescription: x.itemDescription,
        rank: x.rank,
      } as AttributeDefinitionValueListItem;
    });

    if (uiType == AttributeUiType.Number) {
      writeModel.min = this.min;
      writeModel.max = this.max;
    }

    if (uiType === AttributeUiType.Complex) {
      writeModel.subFieldList = this.subFieldList.map((x) => {
        return {
          subFieldDefinitionId: x.subFieldDefinitionId,
          subFieldDescription: x.subFieldDescription,
          rank: x.rank,
          required: x.required,
        } as AttributeDefinitionSubFieldItem;
      });
    }

    return writeModel;
  }

  static parse(obj: any): AttributeDefinition {
    let attributeDefinitionValueListItems = new Array<AttributeDefinitionValueListItem>();
    _.each(obj.valueList, function (attributeDefinitionValueListItem) {
      attributeDefinitionValueListItems.push(
        AttributeDefinitionValueListItem.parse(attributeDefinitionValueListItem)
      );
    });
    let subFieldList = obj.subFieldList?.map((subField) => {
      return AttributeDefinitionSubFieldItem.parse(subField);
    });
    return new AttributeDefinition(
      obj.id,
      obj.entityType,
      obj.uiType,
      obj.name,
      obj.description,
      obj.adminDocumentation,
      obj.filterable,
      obj.featured,
      obj.required,
      obj.isReadOnly,
      obj.isHidden,
      obj.hasVisualization,
      obj.showOnCreate,
      obj.isSubField,
      obj.min,
      obj.max,
      obj.allowsUserAddedValues,
      attributeDefinitionValueListItems,
      subFieldList,
      obj.workspaces,
      obj.createdBy,
      obj.createdOn,
      obj.updatedBy,
      obj.updatedOn,
      obj.isArchived
    );
  }

  static orderByNameCompareFn(def1: AttributeDefinition, def2: AttributeDefinition): number {
    if (def1.name > def2.name) return 1;
    if (def2.name > def1.name) return -1;
    return 0;
  }

  static getSearchValueType(item: AttributeDefinition): SearchValueType {
    if (item.isEnumerated) {
      return SearchValueType.MultiValue;
    }
    if (item.isOfUiType([AttributeUiType.LongAnswer, AttributeUiType.ShortAnswer])) {
      return SearchValueType.String;
    }
    if (item.isOfUiType([AttributeUiType.Number, AttributeUiType.StarRating])) {
      return SearchValueType.Number;
    }
    if (item.uiType == AttributeUiType.Date) {
      return SearchValueType.Date;
    }
    if (item.uiType == AttributeUiType.DateRange) {
      return SearchValueType.DateRange;
    }
    if (item.uiType == AttributeUiType.Document) {
      return SearchValueType.EmptyOperator;
    }

    return null;
  }
}
